import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { RiskRiskmapActions } from './risk-riskmap.actions';
import { RiskmapRiskService } from '../../service/riskmap-risk.service';
import { MessageService } from 'primeng/api';
import { graphQlErrorMessage } from '../../shared/graphql-error.util';


@Injectable()
export class RiskRiskmapEffects {

  loadRiskMapTree$ = createEffect(
    (rs: RiskmapRiskService = inject(RiskmapRiskService)) => {
      return this.actions$.pipe(

        ofType(RiskRiskmapActions.loadRiskMap),
        mergeMap(({ campaignId, parameters, riskId, valueChain, fields, legalEntitiesIds }) =>
          rs.getRiskmapRisk(campaignId, riskId, valueChain, parameters, legalEntitiesIds).pipe(
            map((riskMap) => {
              return RiskRiskmapActions.loadRiskMapSuccess({
                riskMap: riskMap,
                fields
              });
            }),
            catchError((error) => of(RiskRiskmapActions.loadRiskMapFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllRiskMapTree$ = createEffect(
    (rs: RiskmapRiskService = inject(RiskmapRiskService)) => {
      return this.actions$.pipe(
        ofType(RiskRiskmapActions.loadAllRiskMap),
        mergeMap(({ campaignId, parameters, riskId, valueChain, fields, legalEntitiesIds }) =>
          rs.getRiskmapRiskFilters(valueChain).pipe(
            mergeMap((riskMap) => {
              const successAction = RiskRiskmapActions.loadAllRiskMapSuccess({
                riskMap: riskMap,
                fields
              });

              const anotherAction = RiskRiskmapActions.loadRiskMap({
                campaignId,
                riskId,
                valueChain,
                fields,
                parameters,
                legalEntitiesIds
              });

              return [successAction, anotherAction]; // <-- Return multiple actions
            }),
            catchError((error) =>
              of(RiskRiskmapActions.loadAllRiskMapFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );


  updateRiskMapRisk$ = createEffect(
    (
      rs: RiskmapRiskService = inject(RiskmapRiskService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(RiskRiskmapActions.updateRiskMap),
        mergeMap(({ campaignId, legalEntityId, processId, riskId, riskMapId, value, parameterName, cptIds, legalEntitiesId, filters }) =>
          rs.updateRiskmapRisk(campaignId, legalEntityId, processId, riskId, riskMapId, value, legalEntitiesId, filters).pipe(
            mergeMap((riskMap) => [
              RiskRiskmapActions.updateRiskMapSuccess({
                riskMap: riskMap,
                parameterName,
                cptIds
              })
              // RiskmaptreeActions.processRelevanceTree({ campaignId, legalEntityId, processId }) // Seconda action
            ]),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Risk Map Error',
                detail: graphQlErrorMessage(error, 'Unable to update the risk map'),
                life: 10000
              });
              return of(RiskRiskmapActions.updateRiskMapFailure({ error, cptIds, parameterName }));
            })
          )
        )
      );
    },
    { functional: true }
  );


  exportRiskmapRisk$ = createEffect(
    (as: RiskmapRiskService = inject(RiskmapRiskService)) => {
      return this.actions$.pipe(
        ofType(RiskRiskmapActions.exportRiskMapRiskTree),
        mergeMap(({ campaignId, riskId, parameters, timeZone, valueChain, legalEntitiesIds }) =>
          as.downloadRiskRiskmap(campaignId, riskId, parameters, timeZone, valueChain, legalEntitiesIds
          ).pipe(
            map((file) => {
              this.downloadFile(
                file!.data![0]!.content,
                file!.data![0]!.filename
              );
              return RiskRiskmapActions.exportRiskMapRiskTreeSuccess({
                riskMap: file
              });
            }),
            catchError((error) => of(RiskRiskmapActions.exportRiskMapRiskTreeFailure({ error })))
          )
        )
      )
    }
  )

  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type); // Cambia il tipo MIME
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "fileExport";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }


  constructor(private actions$: Actions) { }
}
